import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import {
  buildNameLikeSelectOptions,
  comboBoxOptionsOf,
  emptySelectOption,
  requiredSelectOption,
} from '@/components/ui/multi-select-utils.js';
import { useAllLearningPaths } from '@/lib/api-call-hooks.js';
import { bulkUpdateStudentProfiles } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { InvalidStartingLearningPathNames } from '@airreading/common';
import { OnboardingLearningPathName, StudentOfSchool } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { z } from 'zod';

interface BulkAssignStartingLessonsProps {
  students: StudentOfSchool[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({
  learningPath: requiredSelectOption('Please select a learning path'),
  startingLesson: requiredSelectOption('Please select a starting lesson'),
});

export const BulkAssignStartingLessons = ({ students, trigger, onSuccess }: BulkAssignStartingLessonsProps) => {
  const { toast } = useToast();
  const { learningPaths = [], isPending, error } = useAllLearningPaths();

  const { mutateAsync } = useMutation({
    mutationFn: bulkUpdateStudentProfiles,
  });
  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { learningPath, startingLesson } = formData;
    return mutateAsync({
      studentIds: students.map(({ id }) => id),
      options: {
        level: {
          learningPathId: learningPath.value,
          startingLessonId: startingLesson.value,
        },
      },
    })
      .then(() => {
        toast({
          title: `Updated starting lesson for ${students.length} student(s)`,
        });
        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'Error updating starting lesson',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  const learningPathOptions: MultiSelectOption[] = learningPaths
    .filter((lp) => !InvalidStartingLearningPathNames.includes(lp.name as OnboardingLearningPathName))
    .map(buildNameLikeSelectOptions);

  return (
    <FormModal
      trigger={trigger}
      modalTitle={`Assign starting lesson`}
      formSchema={formSchema}
      onSubmit={onSubmit}
      defaultValues={{
        learningPath: emptySelectOption,
        startingLesson: emptySelectOption,
      }}
    >
      {({ form }) => {
        const { watch, setValue } = form;
        const selectedLearningPathId = watch('learningPath')?.value;
        const selectedLearningPath = learningPaths.find((lp) => lp.id === selectedLearningPathId);
        const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
          ({ lesson }) => ({
            label: lesson.name,
            value: lesson.id,
          }),
        );

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const sub = watch((_, { name }) => {
            if (name === 'learningPath') {
              setValue('startingLesson', emptySelectOption);
            }
          });
          return () => sub.unsubscribe();
        }, [setValue, watch]);

        if (isPending) {
          return <LoaderIcon />;
        }
        if (error) {
          return (
            <div className="flex items-center gap-2">
              <Info className="text-red-500" />
              <span>{airServerErrorFrom(error)?.message}</span>
            </div>
          );
        }
        return (
          <div className="flex flex-col gap-2">
            <p>This will assign the selected starting lesson to all selected students.</p>
            <FormComboBox
              name="learningPath"
              label="Learning path"
              placeholder="Select a learning path"
              form={form}
              {...comboBoxOptionsOf(learningPathOptions)}
            />

            <FormComboBox
              form={form}
              label="Starting lesson"
              name="startingLesson"
              placeholder="Select starting lesson"
              {...comboBoxOptionsOf(selectableLessons)}
            />
          </div>
        );
      }}
    </FormModal>
  );
};
